import type { PublicBusinessConfig } from '@kizen/api/business';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type { Drop, WithRequired } from './types';

import { getPublicBusinessConfig } from '@kizen/api/business';
import { getResource } from './resource';
import { curry1 } from './utils';

type PublicBusinessConfigQuery<TData = PublicBusinessConfig> = WithRequired<
  QueryObserverOptions<
    PublicBusinessConfig,
    DefaultError,
    TData,
    PublicBusinessConfig,
    [Resource<Readonly<['public', 'business', 'config-options']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const publicBusinessConfigQuery = <TData = PublicBusinessConfig>(
  axios: AxiosInstance,
  options?: Omit<PublicBusinessConfigQuery<TData>, 'queryFn' | 'queryKey'>
): PublicBusinessConfigQuery<TData> => {
  const path = ['public', 'business', 'config-options'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;

      return getPublicBusinessConfig(axios, { signal });
    },
    ...options,
  };
};

class Queries {
  public publicBusinessConfigQuery: <TData = PublicBusinessConfig>(
    ...args: Drop<Parameters<typeof publicBusinessConfigQuery<TData>>>
  ) => PublicBusinessConfigQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.publicBusinessConfigQuery = curry1(publicBusinessConfigQuery)(axios);
  }
}

export type BusinessQueries = Queries;

export const defBusinessQueries = (axios: AxiosInstance): BusinessQueries => {
  return new Queries(axios);
};
